import type { FC, ReactNode } from 'react';
import { toast } from 'sonner';
import { Icon, type IconName } from '../assets/icons';
import { cn } from '../lib/cn';
import { copyToClipboard } from '../lib/utils';
import { Loader, Typography } from '../ui';

export type InfoCardItem = {
  id: string;
  label: ReactNode | string;
  icon?: IconName;
  copyable?: boolean;
};

export type InfoCardProps = {
  list: InfoCardItem[];
  height?: number | string;
  loading?: boolean;
};

export const InfoCard: FC<InfoCardProps> = ({
  list,
  height,
  loading = false,
}) => {
  if (loading) {
    return (
      <div
        className={cn(
          'bg-neutral-1 border-neutral-4 flex w-full min-w-0 items-center justify-center border p-4',
          height && `h-[${height}px]`
        )}
      >
        <Loader />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'bg-neutral-1 border-neutral-4 w-full min-w-0 border p-4',
        height && `h-[${height}px]`
      )}
    >
      <ol className="flex flex-col gap-2">
        {list.map(({ icon, id, label, copyable = false }) => (
          <li
            key={id}
            className={cn(
              'flex items-center gap-2',
              copyable && 'cursor-pointer'
            )}
            onClick={() =>
              typeof label === 'string' &&
              copyable &&
              copyToClipboard(label)
                .then(() => toast.success('Copied to clipboard!'))
                .catch(() => toast.error('Failed to copy. Please try again.'))
            }
          >
            {icon && (
              <Icon className="text-primary-text" size={20} name={icon} />
            )}
            {typeof label === 'string' ? (
              <Typography noWrap title={label}>
                {label}
              </Typography>
            ) : (
              label
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};
